import React, { useState, useEffect, useRef, useCallback } from 'react';
import Container from './Container';
import { getCrystalColors } from '../constants/colors';
import { combineColors, updateRevealedState } from './gameLogic';

const PLAYBACK_SPEEDS = [1, 2, 5, 10, 20, 100];

const SolvePlayback = ({ 
  playbackData, 
  onClose, 
  theme, 
  parentIsDarkMode,
  level,
  isHardMode 
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentMoveIndex, setCurrentMoveIndex] = useState(0);
  const [speedIndex, setSpeedIndex] = useState(0);
  const [containers, setContainers] = useState([]);
  const [showPerformanceWarning, setShowPerformanceWarning] = useState(false);
  
  const animationFrameRef = useRef(null);
  const startTimeRef = useRef(null);
  const pausedTimeRef = useRef(0);
  const lastFrameTimeRef = useRef(0);
  const executedMovesRef = useRef(new Set());
  
  const colors = getCrystalColors(parentIsDarkMode);
  const currentSpeed = PLAYBACK_SPEEDS[speedIndex];
  const totalMoves = playbackData?.moves?.length || 0;

  // Initialize containers with the starting state
  useEffect(() => {
    if (playbackData?.initialContainers) {
      setContainers(JSON.parse(JSON.stringify(playbackData.initialContainers)));
      executedMovesRef.current.clear();
      setCurrentMoveIndex(0);
    }
  }, [playbackData]);

  // Performance monitoring
  const checkPerformance = useCallback(() => {
    const now = performance.now();
    const timeSinceLastFrame = now - lastFrameTimeRef.current;
    lastFrameTimeRef.current = now;
    
    // If frame time is too high (low FPS), show warning
    if (timeSinceLastFrame > 50 && currentSpeed > 10) { // Less than 20 FPS
      setShowPerformanceWarning(true);
    }
  }, [currentSpeed]);

  // Execute a single move
  const executeMove = useCallback((move) => {
    setContainers(prevContainers => {
      const newContainers = prevContainers.map(container => [...container]);
      
      // Get the crystal from the source tube
      const movingCrystal = newContainers[move.fromTube].pop();
      
      if (movingCrystal) {
        // Add to destination tube
        newContainers[move.toTube].push(movingCrystal);
        
        // Apply game logic (combining and revealing)
        const combinedContainers = combineColors(newContainers);
        const updatedContainers = updateRevealedState(combinedContainers, level, isHardMode);
        
        return updatedContainers;
      }
      
      return newContainers;
    });
    
    executedMovesRef.current.add(move.sequence);
    setCurrentMoveIndex(move.sequence);
  }, [level, isHardMode]);

  // Main playback loop
  const tick = useCallback(() => {
    if (!isPlaying || !playbackData?.moves) return;
    
    checkPerformance();
    
    const now = performance.now();
    if (!startTimeRef.current) {
      startTimeRef.current = now;
    }
    
    const elapsed = (now - startTimeRef.current - pausedTimeRef.current) * currentSpeed;
    
    // Find moves that should have been executed by now
    const movesToExecute = playbackData.moves.filter(move => 
      move.timeFromStart <= elapsed && 
      !executedMovesRef.current.has(move.sequence)
    );

    // Execute pending moves
    if (currentSpeed >= 20) {
      // At very high speeds, batch process moves to avoid overwhelming the browser
      const batchSize = Math.min(5, movesToExecute.length);
      for (let i = 0; i < batchSize; i++) {
        if (movesToExecute[i]) {
          executeMove(movesToExecute[i]);
        }
      }
    } else {
      // Normal speed - execute all pending moves
      movesToExecute.forEach(executeMove);
    }
    
    // Continue if not finished
    if (executedMovesRef.current.size < totalMoves) {
      animationFrameRef.current = requestAnimationFrame(tick);
    } else {
      setIsPlaying(false);
    }
  }, [isPlaying, playbackData, currentSpeed, executeMove, checkPerformance, totalMoves]);

  // Start/stop playback
  useEffect(() => {
    if (isPlaying) {
      animationFrameRef.current = requestAnimationFrame(tick);
    } else {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    }
    
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isPlaying, tick]);

  // Control functions
  const togglePlayPause = useCallback(() => {
    if (isPlaying) {
      // Pause
      pausedTimeRef.current += performance.now() - (startTimeRef.current || 0);
      setIsPlaying(false);
    } else {
      // Play
      if (executedMovesRef.current.size >= totalMoves) {
        // Restart if finished
        restart();
      } else {
        setIsPlaying(true);
      }
    }
  }, [isPlaying, totalMoves]);

  const cycleFastForward = useCallback(() => {
    const newSpeedIndex = (speedIndex + 1) % PLAYBACK_SPEEDS.length;
    setSpeedIndex(newSpeedIndex);
    setShowPerformanceWarning(false);
  }, [speedIndex]);

  const restart = useCallback(() => {
    setIsPlaying(false);
    setCurrentMoveIndex(0);
    executedMovesRef.current.clear();
    startTimeRef.current = null;
    pausedTimeRef.current = 0;
    
    if (playbackData?.initialContainers) {
      setContainers(JSON.parse(JSON.stringify(playbackData.initialContainers)));
    }
  }, [playbackData]);

  const stepForward = useCallback(() => {
    if (currentMoveIndex < totalMoves) {
      const nextMove = playbackData.moves.find(move => 
        move.sequence === currentMoveIndex + 1
      );
      if (nextMove && !executedMovesRef.current.has(nextMove.sequence)) {
        executeMove(nextMove);
      }
    }
  }, [currentMoveIndex, totalMoves, playbackData, executeMove]);

  const stepBackward = useCallback(() => {
    if (currentMoveIndex > 0) {
      // Reset to beginning and replay up to previous move
      restart();
      setTimeout(() => {
        for (let i = 1; i < currentMoveIndex; i++) {
          const move = playbackData.moves.find(m => m.sequence === i);
          if (move) {
            executeMove(move);
          }
        }
      }, 50);
    }
  }, [currentMoveIndex, restart, playbackData, executeMove]);

  // Keyboard controls
  useEffect(() => {
    const handleKeyPress = (e) => {
      switch(e.key) {
        case ' ':
          e.preventDefault();
          togglePlayPause();
          break;
        case 'ArrowRight':
          e.preventDefault();
          stepForward();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          stepBackward();
          break;
        case 'f':
        case 'F':
          e.preventDefault();
          cycleFastForward();
          break;
        case 'r':
        case 'R':
          e.preventDefault();
          restart();
          break;
        case 'Escape':
          e.preventDefault();
          onClose();
          break;
      }
    };
    
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [togglePlayPause, stepForward, stepBackward, cycleFastForward, restart, onClose]);

  if (!playbackData) {
    return (
      <div className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50`}>
        <div className={`${theme.container} p-8 rounded-lg max-w-md`}>
          <h3 className={`text-xl font-bold ${theme.text} mb-4`}>Loading Playback...</h3>
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
        </div>
      </div>
    );
  }

  const progressPercentage = totalMoves > 0 ? (currentMoveIndex / totalMoves) * 100 : 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex flex-col z-50">
      {/* Header */}
      <div className={`${theme.container} p-4 border-b ${theme.border}`}>
        <div className="flex justify-between items-center">
          <div>
            <h2 className={`text-xl font-bold ${theme.text}`}>
              Solve Playback - Level {level}
            </h2>
            <p className={`${theme.subtext} text-sm`}>
              Completed by {playbackData.completedBy} in {playbackData.formattedTime}
            </p>
          </div>
          <button
            onClick={onClose}
            className={`px-4 py-2 ${theme.button} rounded-lg transition-colors`}
          >
            ✕ Close
          </button>
        </div>
      </div>

      {/* Performance Warning */}
      {showPerformanceWarning && (
        <div className="bg-yellow-100 dark:bg-yellow-900 border-l-4 border-yellow-500 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <span className="text-yellow-500">⚠️</span>
            </div>
            <div className="ml-3">
              <p className="text-sm text-yellow-700 dark:text-yellow-300">
                Playback speed reduced due to performance. Try a lower speed for smoother playback.
              </p>
              <button
                onClick={() => setSpeedIndex(Math.max(0, speedIndex - 2))}
                className="mt-2 text-sm underline text-yellow-700 dark:text-yellow-300"
              >
                Reduce Speed
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Game Board */}
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="flex flex-wrap justify-center items-start gap-4">
          {containers.map((container, index) => (
            <Container
              key={index}
              containerIndex={index}
              crystals={container}
              selected={false}
              onClick={() => {}} // No interaction during playback
              config={{
                containerHeight: 4,
                containerWidth: 60,
                crystalHeight: 50,
                enableAutoSolve: false
              }}
              colors={colors}
              theme={theme}
              isValidTarget={() => false}
              selectedContainerIndex={null}
              level={level}
              isHardMode={isHardMode}
              isPlaybackMode={true}
            />
          ))}
        </div>
      </div>

      {/* Controls */}
      <div className={`${theme.container} p-4 border-t ${theme.border}`}>
        <div className="flex items-center justify-center space-x-4 mb-4">
          {/* Step Backward */}
          <button
            onClick={stepBackward}
            disabled={currentMoveIndex === 0}
            className={`px-3 py-2 ${theme.button} rounded-lg transition-colors disabled:opacity-50`}
            title="Step Backward (←)"
          >
            ⏮️
          </button>

          {/* Play/Pause */}
          <button
            onClick={togglePlayPause}
            className={`px-4 py-2 ${theme.button} rounded-lg transition-colors text-lg`}
            title="Play/Pause (Space)"
          >
            {isPlaying ? '⏸️' : '▶️'}
          </button>

          {/* Step Forward */}
          <button
            onClick={stepForward}
            disabled={currentMoveIndex >= totalMoves}
            className={`px-3 py-2 ${theme.button} rounded-lg transition-colors disabled:opacity-50`}
            title="Step Forward (→)"
          >
            ⏭️
          </button>

          {/* Fast Forward */}
          <button
            onClick={cycleFastForward}
            className={`px-3 py-2 ${theme.button} rounded-lg transition-colors`}
            title="Change Speed (F)"
          >
            ⏩ {currentSpeed}x
          </button>

          {/* Restart */}
          <button
            onClick={restart}
            className={`px-3 py-2 ${theme.button} rounded-lg transition-colors`}
            title="Restart (R)"
          >
            ⏮️ Restart
          </button>
        </div>

        {/* Progress Bar */}
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className={theme.subtext}>Move {currentMoveIndex} of {totalMoves}</span>
            <span className={theme.subtext}>Speed: {currentSpeed}x</span>
          </div>
          <div className={`w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2`}>
            <div
              className="bg-blue-600 h-2 rounded-full transition-all duration-200"
              style={{ width: `${progressPercentage}%` }}
            />
          </div>
        </div>

        {/* Keyboard Shortcuts Help */}
        <div className={`text-xs ${theme.subtext} text-center mt-2`}>
          Space: Play/Pause • ← →: Step • F: Speed • R: Restart • Esc: Close
        </div>
      </div>
    </div>
  );
};

export default SolvePlayback;
